/**
 * Enhanced Unified Student Storage Service - Handles 5000+ students with PWA persistence
 * Combines cache, localStorage, and compression for maximum performance and reliability
 */

import { Student } from '@/services/db/types';

interface StorageEntry {
  id: number;
  data: Student;
  timestamp: string;
  verified: boolean;
}

class UnifiedStudentStorage {
  private readonly STORAGE_KEY = 'nalc_students_unified';
  private readonly CACHE_KEY = 'nalc_cache_students';
  private readonly BACKUP_KEY = 'nalc_backup_students';
  private readonly PERSISTENCE_KEY = 'nalc_persistence_check';
  private cache = new Map<number, Student>();
  private indexMap = new Map<number, number>(); // ID to array index mapping
  private isInitialized = false;
  private persistenceInterval: NodeJS.Timeout | null = null;

  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    
    console.log('🚀 Initializing Enhanced Unified Student Storage for PWA persistence...');
    
    try {
      await this.loadFromStorage();
      this.setupPersistenceMonitoring();
      this.isInitialized = true;
      console.log('✅ Enhanced unified storage initialized successfully');
    } catch (error) {
      console.error('❌ Storage initialization failed:', error);
      this.isInitialized = true; // Continue with empty state
    }
  }

  private setupPersistenceMonitoring(): void {
    // Check persistence every 30 seconds
    this.persistenceInterval = setInterval(async () => {
      try {
        await this.ensurePersistence();
      } catch (error) {
        console.warn('Persistence check failed:', error);
      }
    }, 30000);

    // Also check on visibility change (when user returns to app)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.ensurePersistence();
      }
    });

    // Check on beforeunload to ensure data is saved
    window.addEventListener('beforeunload', () => {
      this.saveToStorageSync();
    });
  }

  private async ensurePersistence(): Promise<void> {
    try {
      // Test if our data is still there
      const testData = localStorage.getItem(this.PERSISTENCE_KEY);
      if (!testData) {
        localStorage.setItem(this.PERSISTENCE_KEY, Date.now().toString());
      }

      // Verify main storage
      const mainData = localStorage.getItem(this.STORAGE_KEY);
      if (!mainData && this.cache.size > 0) {
        console.log('🔄 Main storage lost, restoring from cache...');
        await this.saveToStorage();
      }

      // Create emergency backup if we have data
      if (this.cache.size > 0) {
        const backupData = localStorage.getItem(this.BACKUP_KEY);
        if (!backupData) {
          console.log('🛡️ Creating emergency backup...');
          const students = Array.from(this.cache.values());
          const compressed = this.compressData(students);
          localStorage.setItem(this.BACKUP_KEY, compressed);
        }
      }
    } catch (error) {
      console.error('Error ensuring persistence:', error);
    }
  }

  private saveToStorageSync(): void {
    try {
      if (this.cache.size > 0) {
        const students = Array.from(this.cache.values());
        const compressed = this.compressData(students);
        localStorage.setItem(this.STORAGE_KEY, compressed);
        localStorage.setItem(this.BACKUP_KEY, compressed);
        console.log(`💾 Sync save completed: ${students.length} students`);
      }
    } catch (error) {
      console.error('Sync save failed:', error);
    }
  }

  private async loadFromStorage(): Promise<void> {
    try {
      // Try main storage first
      const mainData = localStorage.getItem(this.STORAGE_KEY);
      if (mainData) {
        const students = this.decompressData(mainData);
        this.updateCacheAndIndex(students);
        console.log(`📦 Loaded ${students.length} students from main storage`);
        return;
      }

      // Try backup if main fails
      const backupData = localStorage.getItem(this.BACKUP_KEY);
      if (backupData) {
        const students = this.decompressData(backupData);
        this.updateCacheAndIndex(students);
        console.log(`📦 Loaded ${students.length} students from backup`);
        return;
      }

      console.log('📦 No existing student data found, starting fresh');
    } catch (error) {
      console.error('Error loading from storage:', error);
    }
  }

  private updateCacheAndIndex(students: Student[]): void {
    this.cache.clear();
    this.indexMap.clear();
    
    students.forEach((student, index) => {
      this.cache.set(student.id, student);
      this.indexMap.set(student.id, index);
    });
  }

  async getAllStudents(): Promise<Student[]> {
    await this.initialize();
    return Array.from(this.cache.values());
  }

  async getStudentById(id: number): Promise<Student | null> {
    await this.initialize();
    
    const student = this.cache.get(id);
    if (student) {
      console.log(`✅ Found student ${id} in cache: ${student.name}`);
      return student;
    }
    
    console.log(`❌ Student ${id} not found in cache`);
    return null;
  }

  async addStudent(studentData: Omit<Student, 'id' | 'createdAt' | 'updatedAt'>): Promise<Student> {
    await this.initialize();
    
    const now = new Date().toISOString();
    const newId = this.generateUniqueId();
    
    const newStudent: Student = {
      ...studentData,
      id: newId,
      createdAt: now,
      updatedAt: now,
      programs: studentData.programs || [],
      documents: studentData.documents || []
    };

    // Add to cache immediately
    this.cache.set(newId, newStudent);
    this.indexMap.set(newId, this.cache.size - 1);
    
    // Save to storage with verification
    await this.saveToStorage();
    
    // Immediate verification
    const verification = await this.getStudentById(newId);
    if (!verification) {
      throw new Error('Student creation verification failed');
    }
    
    console.log(`✅ Student added and verified: ${newStudent.name} (ID: ${newId})`);
    return newStudent;
  }

  async updateStudent(id: number, updateData: Partial<Student>): Promise<Student | null> {
    await this.initialize();
    
    const existingStudent = this.cache.get(id);
    if (!existingStudent) {
      console.log(`❌ Student ${id} not found for update`);
      return null;
    }
    
    const updatedStudent: Student = {
      ...existingStudent,
      ...updateData,
      id: id, // Ensure ID doesn't change
      updatedAt: new Date().toISOString()
    };
    
    this.cache.set(id, updatedStudent);
    await this.saveToStorage();
    
    console.log(`✅ Student updated: ${updatedStudent.name} (ID: ${id})`);
    return updatedStudent;
  }

  async deleteStudent(id: number): Promise<void> {
    await this.initialize();
    
    if (!this.cache.has(id)) {
      console.log(`❌ Student ${id} not found for deletion`);
      return;
    }
    
    this.cache.delete(id);
    this.indexMap.delete(id);
    await this.saveToStorage();
    
    console.log(`✅ Student deleted: ID ${id}`);
  }

  async searchStudents(query: string): Promise<Student[]> {
    await this.initialize();
    const allStudents = Array.from(this.cache.values());
    const lowerQuery = query.toLowerCase();
    
    return allStudents.filter(student => 
      student.name.toLowerCase().includes(lowerQuery) ||
      (student.email && student.email.toLowerCase().includes(lowerQuery)) ||
      (student.contactNumber && student.contactNumber.includes(lowerQuery)) ||
      (student.surname && student.surname.toLowerCase().includes(lowerQuery))
    );
  }

  private generateUniqueId(): number {
    const existingIds = Array.from(this.cache.keys());
    const maxId = existingIds.length > 0 ? Math.max(...existingIds) : 0;
    return maxId + 1;
  }

  private async saveToStorage(): Promise<void> {
    try {
      const students = Array.from(this.cache.values());
      const compressedData = this.compressData(students);
      
      // Clear old data first to make space
      this.clearOldData();
      
      // Try to save to main storage with multiple attempts
      let saved = false;
      let attempts = 0;
      const maxAttempts = 3;
      
      while (!saved && attempts < maxAttempts) {
        try {
          localStorage.setItem(this.STORAGE_KEY, compressedData);
          saved = true;
          console.log(`💾 Saved ${students.length} students to main storage (attempt ${attempts + 1})`);
        } catch (quotaError) {
          attempts++;
          console.warn(`📦 Storage attempt ${attempts} failed, optimizing...`);
          
          if (attempts < maxAttempts) {
            // Progressive cleanup
            this.clearOldData();
            await new Promise(resolve => setTimeout(resolve, 100));
          } else {
            // Final attempt with ultra compression
            const ultraCompressed = this.ultraCompressData(students);
            localStorage.setItem(this.STORAGE_KEY, ultraCompressed);
            saved = true;
            console.log(`💾 Saved ${students.length} students with ultra compression`);
          }
        }
      }
      
      // Always save backup asynchronously
      setTimeout(() => {
        try {
          const backupData = this.compressData(students);
          localStorage.setItem(this.BACKUP_KEY, backupData);
          console.log(`🛡️ Backup saved with ${students.length} students`);
        } catch (e) {
          console.warn('Backup save failed, but main storage is available');
        }
      }, 50);
      
    } catch (error) {
      console.error('Error saving to storage:', error);
      throw error;
    }
  }

  private compressData(students: Student[]): string {
    // Standard compression with all fields
    return JSON.stringify(students);
  }

  private ultraCompressData(students: Student[]): string {
    // Ultra compression for quota exceeded scenarios
    const compressed = students.map(s => ({
      i: s.id,
      n: s.name,
      s: s.surname || '',
      a: s.age || 0,
      c: s.contactNumber || '',
      e: s.email || '',
      addr: s.address || '',
      pi: s.profileImage || '',
      p: (s.programs || []).map(p => ({
        t: p.title,
        g: p.category,
        f: p.fee || 0,
        d: p.duration || 30,
        fs: p.feeStatus || 'pending'
      })),
      ca: s.createdAt,
      ua: s.updatedAt
    }));
    
    return JSON.stringify(compressed);
  }

  private decompressData(data: string): Student[] {
    try {
      const parsed = JSON.parse(data);
      
      // Check if it's ultra-compressed format
      if (parsed.length > 0 && parsed[0].i !== undefined) {
        return this.expandUltraCompressed(parsed);
      }
      
      // Standard format
      return parsed.map(student => ({
        ...student,
        programs: student.programs || [],
        documents: student.documents || []
      }));
    } catch (error) {
      console.error('Error decompressing data:', error);
      return [];
    }
  }

  private expandUltraCompressed(data: any[]): Student[] {
    return data.map(s => ({
      id: s.i,
      name: s.n,
      surname: s.s || '',
      fathersName: '',
      age: s.a || 0,
      address: s.addr || '',
      province: '',
      country: '',
      contactNumber: s.c || '',
      email: s.e || '',
      profileImage: s.pi || '',
      qualificationLevel: undefined,
      documents: [],
      institutionAddress: '',
      institutionPhone: '',
      institutionEmail: '',
      authorizedBy: '',
      policyNote: '',
      programs: (s.p || []).map(p => ({
        title: p.t,
        category: p.g,
        fee: p.f || 0,
        enrolledAt: new Date().toISOString(),
        duration: p.d || 30,
        completionDate: new Date(Date.now() + (p.d || 30) * 24 * 60 * 60 * 1000).toISOString(),
        feeStatus: p.fs || 'pending'
      })),
      createdAt: s.ca || new Date().toISOString(),
      updatedAt: s.ua || new Date().toISOString()
    }));
  }

  private clearOldData(): void {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (
        key.startsWith('nalc_temp_') ||
        key.startsWith('nalc_old_') ||
        key.startsWith('_backup_') ||
        key.includes('_cache_old') ||
        key.includes('_deprecated')
      )) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
    
    if (keysToRemove.length > 0) {
      console.log(`🧹 Cleared ${keysToRemove.length} old storage keys`);
    }
  }

  async getStorageStats(): Promise<{
    totalStudents: number;
    storageUsed: string;
    cacheSize: number;
    isHealthy: boolean;
  }> {
    await this.initialize();
    
    const totalStudents = this.cache.size;
    const storageData = localStorage.getItem(this.STORAGE_KEY);
    const storageUsed = storageData ? `${(storageData.length / 1024).toFixed(2)} KB` : '0 KB';
    
    return {
      totalStudents,
      storageUsed,
      cacheSize: this.cache.size,
      isHealthy: this.cache.size > 0 || totalStudents === 0
    };
  }

  clearCache(): void {
    if (this.persistenceInterval) {
      clearInterval(this.persistenceInterval);
      this.persistenceInterval = null;
    }
    this.cache.clear();
    this.indexMap.clear();
    this.isInitialized = false;
    console.log('🧹 Cache and persistence monitoring cleared');
  }

  // Cleanup method for proper shutdown
  destroy(): void {
    this.saveToStorageSync();
    this.clearCache();
  }
}

export const unifiedStudentStorage = new UnifiedStudentStorage();

// Ensure cleanup on page unload
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    unifiedStudentStorage.destroy();
  });
}
